import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import { useAppContext } from '@psl/appComponents/AppContext';
import { Portal } from '@psl/components/modal/Portal';
import { NavigationAction, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { AnimatePresence, MotiView } from 'moti';
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { Easing } from 'react-native-reanimated';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { CSSProp } from 'styled-components';
import { useIsTabletLayout } from '../components/styles/responsive';
import { useBehaviorSubjectUpdater } from '../functions/hooks/useBehaviorSubject';
import { useResponsiveContext } from '../functions/responsive/ResponsiveContext';
import { useModalPresentationContext } from './ModalWrapper';
import { useIsDarkMode } from './UserInterfaceIdiomContext';

const kDefaultWidth = 550;

export type AsideProps = {
  width?: number;
  dismissible?: boolean;
  portal?: boolean;
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  backdropCss?: CSSProp;
  onDismiss?: () => void;
};

export const asideNavigationOptions = () => {};

const kMultiTaskIndicatorWidth = 75;

export const useAsideWidth = (preferredWidth?: number) => {
  const { rpx, dimens } = useResponsiveContext();
  const isTablet = useIsTabletLayout();
  let defaultWidth = rpx(kDefaultWidth);
  if (isTablet) {
    const halfWidth = dimens.width / 2;
    if (Math.abs(halfWidth - defaultWidth) < kMultiTaskIndicatorWidth) {
      if (defaultWidth > halfWidth) {
        defaultWidth = halfWidth + kMultiTaskIndicatorWidth;
      } else {
        defaultWidth = halfWidth - kMultiTaskIndicatorWidth;
      }
    }
  } else if (Platform.OS === 'web') {
    defaultWidth = Math.min(
      dimens.width * 0.85,
      Math.max(defaultWidth, dimens.width * 0.35),
    );
  }
  return preferredWidth ?? defaultWidth;
};

type AsideContextProps = { width: number };

const AsideContext = createContext<AsideContextProps>(null as any);

export const useAsideContext = () => useContext(AsideContext);

export const useIsAsideLayout = () => {
  return useContext(AsideContext) != null;
};

export const Aside = memo((props: AsideProps) => {
  const { top, bottom } = useSafeAreaInsets();
  const width = useAsideWidth(props.width);
  // to control the react native modal
  const [open, setOpen] = useState(true);
  const [show, setShow] = useState(true);
  const isDarkMode = useIsDarkMode();
  const { isAsideActive$ } = useAppContext();

  const navigation = useNavigation<StackNavigationProp<any>>();
  const index = useRef(navigation.getState().index).current;
  const continueAction = useRef<NavigationAction>();

  const { modalPresented$ } = useModalPresentationContext();
  const updateModalPresented = useBehaviorSubjectUpdater(modalPresented$);

  useEffect(() => {
    isAsideActive$.next(true);
    return () => isAsideActive$.next(false);
  }, [isAsideActive$]);

  useEffect(() => {
    if (!show) {
      return;
    }
    return navigation.addListener('beforeRemove', e => {
      e.preventDefault();
      continueAction.current = e.data.action;
      setShow(false);
    });
  }, [navigation, show]);

  useEffect(() => {
    updateModalPresented(ids => [...new Set([...ids, index])]);
    return () => {
      updateModalPresented(ids => {
        const set = new Set(ids);
        set.delete(index);
        return [...set];
      });
    };
  }, [index, updateModalPresented]);

  const onDismissed = useCallback(() => {
    setOpen(false);
    if (continueAction.current != null) {
      navigation.dispatch(continueAction.current);
    } else {
      navigation.goBack();
    }
  }, [navigation]);

  const onDismiss = useCallback(() => {
    if (props.dismissible !== false) {
      if (props.onDismiss) {
        props.onDismiss();
      }
      setShow(false);
    }
  }, [props]);

  const contextValue = useMemo(() => ({ width }), [width]);

  return (
    <Portal open={open} enabled={props.portal}>
      <AsideContext.Provider value={contextValue}>
        <AnimatePresence onExitComplete={onDismissed}>
          {show && (
            <>
              <MotiView
                from={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: 'timing', duration: 300 }}
                style={StyleSheet.absoluteFill}
                exitTransition={{
                  type: 'spring',
                  overshootClamping: true,
                }}
                css={`
                  padding-top: ${top}px;
                  background-color: rgba(0, 0, 0, 0.6);
                  ${props.backdropCss};
                `}
              >
                <TouchableWithoutFeedback onPress={onDismiss}>
                  <View style={StyleSheet.absoluteFill} />
                </TouchableWithoutFeedback>
              </MotiView>

              <KeyboardAvoidingView
                behavior={'padding'}
                css={`
                  flex: 1;
                  width: ${width}px;
                  position: absolute;
                  right: 0;
                  top: 0;
                  bottom: 0;
                `}
              >
                <MotiView
                  css={`
                    background-color: ${isDarkMode ? '#262626' : '#fff'};
                    overflow: hidden;
                    align-self: center;
                    width: ${width}px;
                    flex: 1;
                  `}
                  style={[props.style]}
                  from={{ translateX: width }}
                  animate={{ translateX: 0 }}
                  transition={{
                    type: 'timing',
                    duration: 250,
                    easing: Easing.in(Easing.linear),
                  }}
                  exit={{ translateX: width }}
                  exitTransition={{
                    type: 'spring',
                    overshootClamping: true,
                  }}
                >
                  <SafeAreaInsetsContext.Provider
                    value={{
                      top: 0,
                      right: 0,
                      bottom,
                      left: 0,
                    }}
                  >
                    <ActionSheetProvider>{props.children}</ActionSheetProvider>
                  </SafeAreaInsetsContext.Provider>
                </MotiView>
              </KeyboardAvoidingView>
            </>
          )}
        </AnimatePresence>
      </AsideContext.Provider>
    </Portal>
  );
});
